import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { createUploadS3 } from "@/lib/s3/uploadAudioS3";
import { addFile } from "@/services/files";
import getExtension from "@/lib/getExtension";
import type { Upload } from "@aws-sdk/lib-storage";
import * as Sentry from "@sentry/react";
import { BackgroundVisitUploadStatus } from "@/common/enums/backgroundVisitUploadStatus";

export interface VisitToUpload {
  file: Blob;
  userId: number;
  duration: number;
  style: string;
  format: string;
  exam: string;
  template: string;
  macros?: string;
  customTemplateId: number | null;
}

export interface BackgroundVisitUpload {
  id: string;
  createdAt: Date;
  status: BackgroundVisitUploadStatus;
  controller: Upload;
  data: VisitToUpload;
  progress: number;
}

export interface BackgroundVisitUploadStoreState {
  uploads: BackgroundVisitUpload[];
  enqueueVisitToUpload: (
    visitToUpload: VisitToUpload,
    options?: { autoStart?: boolean }
  ) => Promise<void>;
  cancelVisitToUpload: (id: string) => void;
  startVisitToUpload: (id: string, attempt?: number) => Promise<void>;
}

export const useBackgroundVisitUploadStore =
  create<BackgroundVisitUploadStoreState>()((set, get) => {
    const setStatusById = (
      id: string,
      status: BackgroundVisitUploadStatus
    ): void => {
      set((state) => ({
        uploads: state.uploads.map((upload) =>
          upload.id === id ? { ...upload, status: status } : upload
        ),
      }));
    };

    const setProgressById = (id: string, progress: number): void => {
      set((state) => ({
        uploads: state.uploads.map((upload) =>
          upload.id === id ? { ...upload, progress: progress } : upload
        ),
      }));
    };

    const getBackgroundUploadById = (
      id: string
    ): BackgroundVisitUpload | undefined => {
      return get().uploads.find((upload) => upload.id == id);
    };

    return {
      uploads: [],
      startVisitToUpload: async (id: string, attempt = 1): Promise<void> => {
        const maxRetries = 3;
        const currentUpload = getBackgroundUploadById(id);
        if (currentUpload) {
          setStatusById(id, BackgroundVisitUploadStatus.UPLOADING);
          try {
            currentUpload.controller.on("httpUploadProgress", (progress) => {
              if (progress.loaded && progress.total) {
                const percent = Math.floor(
                  (progress.loaded / progress.total) * 100
                );
                setProgressById(id, percent);
              }
            });
            await currentUpload.controller.done();
            addFile({
              awsBucket: import.meta.env.VITE_S3_BUCKET_URL || "",
              name: currentUpload.id,
              ...currentUpload.data,
            }).catch(() => {
              // soft catch beacuse this is controlled by backend
            });
            setStatusById(id, BackgroundVisitUploadStatus.DONE);
          } catch (error) {
            if (error instanceof Error && error.name == "AbortError") {
              setStatusById(id, BackgroundVisitUploadStatus.CANCELLED);
            } else {
              if (attempt < maxRetries) {
                await get().startVisitToUpload(id, attempt + 1);
              } else {
                Sentry.captureException(error, {
                  tags: {
                    module: "background-visit-upload",
                  },
                  extra: {
                    fileId: id,
                    attempt: attempt,
                    size: currentUpload.data.file.size,
                    format: currentUpload.data.file.type,
                    userId: currentUpload.data.userId,
                  },
                });
                setStatusById(id, BackgroundVisitUploadStatus.FAILED);
              }
            }
          }
        }
      },
      enqueueVisitToUpload: async (
        visitToUpload,
        options = { autoStart: true }
      ): Promise<void> => {
        const uid = uuidv4();
        const extension = getExtension(visitToUpload.file);
        const fileKey = `${visitToUpload.userId}-${uid}.${extension}`;
        const newUpload: BackgroundVisitUpload = {
          id: fileKey,
          createdAt: new Date(),
          data: visitToUpload,
          controller: await createUploadS3(visitToUpload.file, fileKey),
          status: BackgroundVisitUploadStatus.PENDING,
          progress: 0,
        };
        set({ uploads: [...get().uploads, newUpload] });
        if (options.autoStart) {
          void get().startVisitToUpload(newUpload.id);
        }
      },
      cancelVisitToUpload: (id): void => {
        const currentUpload = get().uploads.find((upload) => upload.id == id);
        if (currentUpload) {
          setStatusById(
            currentUpload.id,
            BackgroundVisitUploadStatus.CANCELLED
          );
        }
        void currentUpload?.controller.abort();
      },
    };
  });
